import { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'

function TestimonialCard({ data, index, inView }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [openItem, setOpenItem] = useState(null)

  return (
    <motion.blockquote
      className="testimonial-card"
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      whileHover={{ scale: 1.01, filter: 'brightness(1.08)' }}
      transition={{ duration: 1, delay: 0.2 + index * 0.15, ease: [0.16, 1, 0.3, 1] }}
    >
      <p className="testimonial-quote">&ldquo;{data.quote}&rdquo;</p>
      <footer className="testimonial-footer">
        <span className="testimonial-name">{data.name}{data.age ? `, ${data.age}` : ''}</span>
        {data.detail && <span className="testimonial-detail">{data.detail}</span>}
      </footer>

      <button
        className={`sci-toggle ${open ? 'open' : ''}`}
        onClick={() => { setOpen(!open); if (open) setOpenItem(null) }}
        aria-expanded={open}
      >
        <span>{t('testimonials.whyPerceived', { name: data.name })}</span>
        <motion.span
          className="sci-chevron"
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3, ease: [0.33, 1, 0.68, 1] }}
        >
          &#8964;
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="sci-accordion"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.33, 1, 0.68, 1] }}
          >
            {data.breakdown.map((item, i) => (
              <div className="sci-accordion-item" key={i}>
                <button
                  className="sci-accordion-trigger"
                  onClick={() => setOpenItem(openItem === i ? null : i)}
                  aria-expanded={openItem === i}
                >
                  <span className="sci-perception">{item.perception}</span>
                  <span className="sci-ingredient">{item.ingredient}</span>
                  <motion.span
                    className="sci-chevron"
                    animate={{ rotate: openItem === i ? 180 : 0 }}
                    transition={{ duration: 0.3, ease: [0.33, 1, 0.68, 1] }}
                  >
                    &#8964;
                  </motion.span>
                </button>
                <AnimatePresence>
                  {openItem === i && (
                    <motion.div
                      className="sci-accordion-content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.33, 1, 0.68, 1] }}
                    >
                      <p>{item.explanation}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.blockquote>
  )
}

export default function Testimonials() {
  const { t } = useTranslation()
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-100px' })

  const testimonials = t('testimonials.entries', { returnObjects: true })

  return (
    <section className="testimonials-section" ref={ref}>
      <motion.h2
        className="testimonials-heading"
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      >
        {t('testimonials.heading')}
      </motion.h2>

      <div className="testimonials-grid">
        {testimonials.map((entry, i) => (
          <TestimonialCard key={i} data={entry} index={i} inView={inView} />
        ))}
      </div>
    </section>
  )
}
